/**
 * Karbon-style rich email composer.
 *  - TipTap editor (bold/italic/underline/lists/link)
 *  - To / Cc / Bcc chip inputs (uses AddressBookInput)
 *  - Multi-attachment upload (FileReader → base64 → backend)
 *  - Dynamic signature insertion (from /api/gmail/signature)
 *  - Send now OR schedule-send (date+time picker; backend cron-style worker
 *    sends the Gmail draft at the chosen UTC instant)
 *  - Preserves [XXXXX] subject-anchor token for task-replies
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import api from "@/lib/api";
import AddressBookInput from "@/components/AddressBookInput";
import { sanitizeEmailHtml } from "@/lib/sanitizeHtml";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
// Iter 104 — extensions added for text colour + strike toolbar buttons.
// StarterKit (v3) already ships Strike, Underline and Link, so we only
// add TextStyle + Color here. TextStyle is the dependency Color needs
// to write arbitrary `color: …` inline styles onto the active selection.
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
// Iter 110f — Image extension so signature logos / inline base64
// images survive the round-trip through Tiptap's schema. Without
// this the signature `<img>` tags were silently stripped on
// `editor.commands.setContent(...)`, so users with a logo in their
// Profile signature saw text-only signatures in the composer.
import Image from "@tiptap/extension-image";
import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import MentionList from "@/components/email/MentionList";
import SchedulePopover from "@/components/SchedulePopover";
import ColorPalettePopover from "@/components/ColorPalettePopover";
import ClientContactChips, { appendEmail } from "@/components/ClientContactChips";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2,
  Paperclip, X, Send, Clock, ChevronDown, Trash2, CalendarDays,
  Strikethrough,
} from "lucide-react";

const ROW_CLS = "flex items-center gap-3 border-b border-slate-200 py-2";
const ROW_LABEL_CLS = "text-[13px] text-slate-500 w-12 shrink-0";

// Build a local-isoformat (no tz) and convert to UTC ISO for the backend.
function localToUtcIso(dateStr, timeStr) {
  // dateStr = "YYYY-MM-DD", timeStr = "HH:MM" (24h, in the user's local tz)
  if (!dateStr || !timeStr) return null;
  const dt = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString(); // UTC
}

function ToolbarBtn({ active, onClick, children, title, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`p-1.5 rounded text-[13px] transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );
}

export default function RichEmailComposer({
  initial = {},
  gmailConnected,
  onSent,
  onCancel,
  // Iter 67 — Draft autosave.
  draftId: draftIdProp = "",
  onDraftSaved,
  // Iter 68 — emit subject changes upwards so the floating panel header /
  // minimised pill title can stay in sync.
  onSubjectChange,
  // Iter 84 — Audit-trail token. Non-editable chip rendered after the
  // subject input. Computed by the parent (FloatingComposeRoot) via
  // GET /api/audit-token from task_id / client_id.
  //   { kind: 'task'|'client', audit_seq: int, token: '[Txxx]', client_name?: str }
  auditToken = null,
  // Iter 84 — Explicit client tag, sent through to the backend so the
  // outbound interceptor can apply the [Cxxx] token when there's no task.
  tagClientId = "",
}) {
  // Address / subject state
  const [to, setTo] = useState(initial.to || "");
  const [cc, setCc] = useState(initial.cc || "");
  const [bcc, setBcc] = useState(initial.bcc || "");
  const [showCcBcc, setShowCcBcc] = useState(Boolean(initial.cc || initial.bcc));
  const [subject, setSubject] = useState(initial.subject || "");
  // Thread context (when replying)
  const threadRef = useRef({
    thread_id: initial.thread_id || null,
    in_reply_to: initial.in_reply_to || "",
    references: initial.references || "",
    task_id: initial.task_id || null,
    // Iter 66 — Carry the reply/forward parent through to the backend so
    // it can stitch the quoted chain into the outgoing HTML. Without
    // these two fields, the backend can't find the parent and the chain
    // is silently dropped.
    original_message_id: initial.original_message_id || "",
    compose_mode: initial.compose_mode || "",
  });
  // Attachments — kept in memory as { filename, mime_type, size, data_b64 }.
  // Iter 82 — Forwarded attachments are stored in the SAME array but carry
  // `attachment_id` + `source_gmail_id` instead of `data_b64`. The backend
  // distinguishes them by which keys are present and fetches the bytes
  // from Gmail server-side.
  const [attachments, setAttachments] = useState(() =>
    (initial.forward_attachments || []).map((a) => ({
      filename: a.filename,
      mime_type: a.mime_type || "application/octet-stream",
      size: a.size || 0,
      attachment_id: a.attachment_id,
      source_gmail_id: a.source_gmail_id,
      forwarded: true,  // UI hint — render with a small "forwarded" chip
    }))
  );
  const fileInputRef = useRef(null);
  // Signature
  const [signatureHtml, setSignatureHtml] = useState("");
  const [signatureLoaded, setSignatureLoaded] = useState(false);
  const [signatureApplied, setSignatureApplied] = useState(false);
  // Schedule
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);  // Date object now (shadcn Calendar)
  const [scheduleTime, setScheduleTime] = useState("");
  const [sending, setSending] = useState(false);
  // Undo-send safety net: after the user clicks Send, hold for 5 seconds
  // with a banner before firing the API. If they click Undo we restore the
  // composer state and abort. Skipped for scheduled sends (they're already
  // delayed by definition).
  const [pendingSend, setPendingSend] = useState(null);  // { remaining, cancel }
  const pendingTimerRef = useRef(null);

  // Pull signature once on mount.
  useEffect(() => {
    api.get("/gmail/signature")
      .then((r) => setSignatureHtml(r.data?.signature_html || ""))
      .finally(() => setSignatureLoaded(true));
  }, []);

  // Keep a stable ref to setTo so the mention suggestion can auto-append
  // even though the extension config is captured once at editor init.
  const toRef = useRef(to);
  useEffect(() => { toRef.current = to; }, [to]);
  const appendToAddress = (item) => {
    const formatted = item.name ? `${item.name} <${item.email}>` : item.email;
    const tokens = (toRef.current || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    const already = tokens.some((t) => t.toLowerCase().includes(item.email.toLowerCase()));
    if (already) return;
    const next = [...tokens, formatted].join(", ");
    setTo(next);
  };

  // TipTap editor.
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        // StarterKit v3 ships its own Link — just pass options through
        // so we don't double-register and trigger the "Duplicate
        // extension names" warning.
        link: { openOnClick: false, autolink: true },
      }),
      // Iter 104 — TextStyle must come BEFORE Color (it provides the
      // wrapping <span> the Color mark applies its `style="color:..."` to).
      TextStyle,
      Color,
      // Iter 110f — Image (data: + http(s) URLs) so signature logos
      // render in the compose body. Mirrors the SignatureEditor config
      // so what users see in Profile matches what they see when they
      // hit Compose / Reply / Forward.
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: { class: "compose-img max-h-24 inline-block" },
      }),
      Placeholder.configure({ placeholder: "Write your message…" }),
      Mention.configure({
        HTMLAttributes: {
          class: "bg-blue-100 text-blue-800 rounded px-1 py-0.5 text-[12px] font-medium",
        },
        renderText: ({ node }) => `@${node.attrs.label || node.attrs.id}`,
        suggestion: {
          items: async ({ query }) => {
            if (!query) return [];
            try {
              const r = await api.get(`/address-book?q=${encodeURIComponent(query)}`);
              return (r.data || []).slice(0, 6);
            } catch { return []; }
          },
          render: () => {
            let component;
            let popup;
            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList, {
                  props: { ...props, query: props.query, onPick: appendToAddress },
                  editor: props.editor,
                });
                if (!props.clientRect) return;
                popup = tippy("body", {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  placement: "bottom-start",
                });
              },
              onUpdate(props) {
                component?.updateProps({ ...props, query: props.query, onPick: appendToAddress });
                if (!props.clientRect) return;
                popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
              },
              onKeyDown(props) {
                if (props.event.key === "Escape") { popup?.[0]?.hide(); return true; }
                return component?.ref?.onKeyDown(props);
              },
              onExit() {
                popup?.[0]?.destroy();
                component?.destroy();
              },
            };
          },
        },
      }),
    ],
    content: initial.body_html || "",
    editorProps: {
      attributes: {
        // Iter 104 — explicit list-style on ul/ol because Tailwind preflight
        // resets them to `list-style: none`. The `prose` class would normally
        // restore them but only when the typography plugin is fully wired —
        // belt-and-braces with `[&_ul]:list-disc` etc. so the editor renders
        // bullets/numbers regardless of the host page's CSS.
        class: "prose prose-sm max-w-none focus:outline-none min-h-[260px] px-4 py-3 text-[13px] leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li>p]:my-0",
        "data-testid": "compose-editor",
      },
    },
  }, []);

  // Insert signature once after both signature + editor are ready. The
  // quoted chain (initial.quote_html) is NOT inserted into Tiptap — its
  // schema would strip the parent body. We render it separately below
  // the editor and concatenate on send. This matches Gmail's mental
  // model (chain always included; click "..." to expand).
  //
  // Iter 86d — ALWAYS append the signature to the end of the editor body
  // (not "only if empty"). The contract surfaced to users on the Profile
  // page is "Inserted automatically at the bottom of every new compose",
  // so a fresh reply with no body should still get the signature, and
  // even a forward with prefilled content should append it underneath
  // (Gmail's behaviour).
  //
  // Iter 105 — Detect previous insertion by SIGNATURE TEXT content
  // rather than by a `close-email-signature` wrapper class, because
  // TipTap's StarterKit schema strips arbitrary <div>s + class attrs
  // on round-trip through setContent. The signatureApplied state flag
  // still guarantees one-shot per mount; the text-content guard
  // protects against duplicate insertion when a draft (already
  // containing the signature) is reopened.
  useEffect(() => {
    if (!editor || !signatureLoaded || signatureApplied) return;
    if (!signatureHtml) { setSignatureApplied(true); return; }
    // Extract the signature's plain text — used to detect duplicates.
    const tmp = document.createElement("div");
    tmp.innerHTML = signatureHtml;
    const sigText = (tmp.textContent || "").replace(/\s+/g, " ").trim();
    const currentText = (editor.getText() || "").replace(/\s+/g, " ").trim();
    if (sigText && currentText.includes(sigText)) {
      // Draft was reopened and already contains the signature — skip.
      setSignatureApplied(true);
      return;
    }
    const currentHtml = editor.getHTML() || "";
    const prelude = currentHtml.trim() && currentHtml.trim() !== "<p></p>"
      ? `${currentHtml}<p>&nbsp;</p>`
      : `<p></p><p>&nbsp;</p>`;
    editor.commands.setContent(`${prelude}${signatureHtml}`);
    try { editor.commands.focus("start"); } catch (_e) { /* ignore */ }
    setSignatureApplied(true);
  }, [editor, signatureLoaded, signatureHtml, signatureApplied]);

  // Iter 66b — toggle for the quoted-chain preview (Gmail's "..." behaviour).
  const [quoteExpanded, setQuoteExpanded] = useState(true);
  const quoteHtml = initial.quote_html || "";

  // Iter 67 — Autosave drafts. We hold the active draft_id in a ref so
  // the save handler doesn't trigger React renders on every save. The
  // backend mints a new id on first save; subsequent saves upsert.
  const draftIdRef = useRef(draftIdProp || "");
  const [draftStatus, setDraftStatus] = useState(""); // "Saving…" | "Saved" | ""
  const saveTimerRef = useRef(null);
  const sentRef = useRef(false);

  // Iter 70 — Refs for Gmail Drafts API sync. We push the local draft to
  // Gmail's `users.drafts.create / .update` on a 15s cadence (vs the 2s
  // local autosave) so it appears in the user's native Gmail Drafts
  // folder and syncs across devices, without spamming the Gmail quota.
  const gmailDraftIdRef = useRef("");
  const lastGmailPushAtRef = useRef(0);

  const saveDraftNow = useCallback(async () => {
    if (!editor || sentRef.current) return;
    const body_html_editor = editor.getHTML();
    const body_text = editor.getText();
    const payload = {
      draft_id: draftIdRef.current || null,
      channel: "email",
      subject,
      to_email: to,
      cc,
      bcc,
      body_html: body_html_editor,
      body_text,
      thread_id: threadRef.current.thread_id || null,
      in_reply_to: threadRef.current.in_reply_to || "",
      references: threadRef.current.references || "",
      original_message_id: threadRef.current.original_message_id || "",
      compose_mode: threadRef.current.compose_mode || "",
      quote_html: quoteHtml,
      task_id: threadRef.current.task_id || null,
      // Iter 82 — Preserve forwarded attachment REFS in the draft autosave
      // so reopening a closed forward draft still ships the parent's
      // attachments. User-uploaded files are NOT persisted in drafts
      // (data_b64 is lost on close — that's the existing behavior).
      attachments_meta: attachments.map((a) => ({
        filename: a.filename, size: a.size, mime_type: a.mime_type,
        attachment_id: a.attachment_id || null,
        source_gmail_id: a.source_gmail_id || null,
        forwarded: !!a.forwarded,
      })),
      // Iter 84 — Carry the explicit client tag through to drafts so a
      // re-opened draft still ships the [Cxxx] token at send time.
      tag_client_id: tagClientId || null,
      gmail_draft_id: gmailDraftIdRef.current || null,
    };
    try {
      setDraftStatus("Saving…");
      const r = await api.post("/drafts", payload);
      if (r.data?.deleted) {
        draftIdRef.current = "";
        gmailDraftIdRef.current = "";
        setDraftStatus("");
      } else if (r.data?.draft_id) {
        const isNew = !draftIdRef.current;
        draftIdRef.current = r.data.draft_id;
        setDraftStatus("Saved");
        if (isNew) onDraftSaved?.(r.data.draft_id);
      }
    } catch (_e) {
      setDraftStatus("Save failed");
      return;
    }
    // Iter 70 — Push to Gmail Drafts on a slower cadence (15s) so it shows
    // up in native Gmail too. Throttled because each call hits Google's
    // quota. We skip if it's an empty draft (no recipient + no body).
    const hasContent = (to.trim() || cc.trim() || bcc.trim() || subject.trim() || body_text.trim());
    const now = Date.now();
    if (!gmailConnected || !hasContent) return;
    if (now - lastGmailPushAtRef.current < 15000 && gmailDraftIdRef.current) return;
    lastGmailPushAtRef.current = now;
    try {
      const gpayload = {
        gmail_draft_id: gmailDraftIdRef.current || undefined,
        to, cc, bcc, subject,
        body_html: body_html_editor,
        body_text,
        in_reply_to: threadRef.current.in_reply_to || "",
        references: threadRef.current.references || "",
        thread_id: threadRef.current.thread_id || null,
      };
      const r = await api.post("/gmail/drafts", gpayload);
      if (r.data?.gmail_draft_id) {
        gmailDraftIdRef.current = r.data.gmail_draft_id;
        // Persist gmail_draft_id back to local row on next save tick.
      }
    } catch (_e) {
      // Silent — local save already succeeded; Gmail push is best-effort.
    }
  }, [editor, to, cc, bcc, subject, attachments, quoteHtml, onDraftSaved, gmailConnected]);

  // Debounced autosave: 2s after any change in the editor content or any
  // of the form fields.
  useEffect(() => {
    if (!editor) return;
    const schedule = () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(saveDraftNow, 2000);
    };
    schedule(); // also runs when to/cc/bcc/subject/attachments change
    editor.on("update", schedule);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      editor.off("update", schedule);
    };
  }, [editor, to, cc, bcc, subject, attachments, saveDraftNow]);

  // Save on unmount (covers minimize-then-close, browser refresh, etc.).
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      // Best-effort sync flush.
      saveDraftNow();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteDraft = useCallback(async () => {
    if (draftIdRef.current) {
      try { await api.delete(`/drafts/${draftIdRef.current}`); } catch (_e) { /* ignore */ }
      draftIdRef.current = "";
    }
    // Iter 70 — Also discard the Gmail-side draft. The backend's
    // /drafts DELETE already cascades, but we call this explicitly here
    // for the "Discard" button code path (which uses no local draft id
    // when the user opens then immediately discards).
    if (gmailDraftIdRef.current) {
      try { await api.delete(`/gmail/drafts/${gmailDraftIdRef.current}`); } catch (_e) { /* ignore */ }
      gmailDraftIdRef.current = "";
    }
  }, []);

  // Iter 109 — Extracted the per-file processing so we can reuse it
  // for the file picker AND for drag-and-drop / clipboard paste.
  const processFiles = async (files) => {
    if (!files.length) return;
    const MAX = 24 * 1024 * 1024; // 24 MB per file (Gmail raw limit ~25 MB total)
    const out = [];
    for (const f of files) {
      if (f.size > MAX) {
        toast.error(`${f.name} is over 24 MB — skipped.`);
        continue;
      }
      const data_b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const s = String(r.result || "");
          // strip the data:<mime>;base64, prefix
          const i = s.indexOf(",");
          resolve(i >= 0 ? s.slice(i + 1) : s);
        };
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      out.push({
        filename: f.name,
        mime_type: f.type || "application/octet-stream",
        size: f.size,
        data_b64,
      });
    }
    if (out.length) {
      setAttachments((prev) => [...prev, ...out]);
      toast.success(out.length === 1 ? `Attached ${out[0].filename}` : `Attached ${out.length} files`);
    }
  };

  const onAttachFiles = async (e) => {
    await processFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  // Iter 109 — Drag-and-drop. The composer is the drop target; the
  // editor's contenteditable would otherwise try to insert the dropped
  // file as text. We add a global drag overlay so the user gets clear
  // visual feedback even when hovering the toolbar or chip strip.
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragCounter = useRef(0);
  const isFileDrag = (e) => {
    const types = e.dataTransfer?.types || [];
    return Array.from(types).includes("Files");
  };
  const onDragEnter = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragCounter.current += 1;
    setIsDraggingFiles(true);
  };
  const onDragOver = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDraggingFiles(false);
  };
  const onDrop = async (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsDraggingFiles(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) await processFiles(files);
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const setLinkPrompt = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  // Validation + send.
  const canSend = useMemo(() => to.trim().length > 0 && !sending && !pendingSend, [to, sending, pendingSend]);

  // Build the full /gmail/send payload from the current composer state.
  // Iter 85 — extracted so `doSend` can build the payload BEFORE the
  // composer unmounts, hand it to the undo bridge as a self-contained
  // closure (no React state reads at commit time).
  const buildSendPayload = ({ scheduleIso = null } = {}) => {
    const editor_html = editor?.getHTML() || "";
    const body_text = editor?.getText() || "";
    const body_html = quoteHtml
      ? `${editor_html}<br><br>${quoteHtml}`
      : editor_html;
    const payload = {
      to, cc, bcc, subject,
      body: body_text,
      body_html,
      thread_id: threadRef.current?.thread_id || null,
      in_reply_to: threadRef.current?.in_reply_to || "",
      references: threadRef.current?.references || "",
      task_id: threadRef.current?.task_id || null,
      original_message_id: threadRef.current?.original_message_id || "",
      compose_mode: threadRef.current?.compose_mode || "",
      attachments,
      tag_client_id: tagClientId || null,
    };
    if (scheduleIso) payload.scheduled_at = scheduleIso;
    return payload;
  };

  // Fire-and-forget Gmail send used by both the scheduled flow (immediate)
  // and the undo-window commit (5s post-Send). Operates ONLY on the
  // captured payload — must NOT read React state because the component
  // may have unmounted in the meantime.
  const commitPayload = async (payload) => {
    try {
      const r = await api.post("/gmail/send", payload);
      if (r.data?.scheduled) {
        toast.success("Scheduled to send.", {
          description: new Date(r.data.scheduled_at).toLocaleString("en-GB"),
        });
      }
      onSent?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send.");
    }
  };

  const performSend = async ({ scheduleIso = null } = {}) => {
    if (!to.trim()) { toast.error("Recipient (To) required."); return; }
    if (!gmailConnected) { toast.error("Connect Gmail before sending."); return; }
    const payload = buildSendPayload({ scheduleIso });
    setSending(true);
    try {
      const r = await api.post("/gmail/send", payload);
      sentRef.current = true;
      await deleteDraft();
      if (r.data?.scheduled) {
        toast.success("Scheduled to send.", {
          description: new Date(r.data.scheduled_at).toLocaleString("en-GB"),
        });
      } else {
        toast.success("Sent.");
      }
      onSent?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send.");
    } finally {
      setSending(false);
    }
  };
  // Iter 85 — UX: close the composer IMMEDIATELY on Send and host the
  // 5-second undo inside the green sonner toast. Old behaviour kept the
  // composer open for 10s with a tiny "Sending in Xs… Undo" banner —
  // poor UX because the user can't move on to the next task. The
  // restructured flow:
  //
  //   1. Capture the full payload + close the composer.
  //   2. Show a green toast with a live "Undo (5s)" action button.
  //   3. After 5s without undo → fire `performSend` (which actually
  //      hits Gmail).
  //   4. If Undo clicked → cancel the timer, re-open the composer with
  //      the captured state intact.
  const doSend = ({ scheduleIso = null } = {}) => {
    if (scheduleIso) { performSend({ scheduleIso }); return; }
    if (!to.trim()) { toast.error("Recipient (To) required."); return; }
    if (!gmailConnected) { toast.error("Connect Gmail before sending."); return; }
    // Capture the full state we'll need to restore if the user hits
    // Undo (composer re-opens with the original fields intact).
    const snapshot = {
      to, cc, bcc, subject,
      body_html: editor?.getHTML() || "",
      attachments,
      tagClientId,
      compose_mode: threadRef.current?.compose_mode || "",
      original_message_id: threadRef.current?.original_message_id || "",
      thread_id: threadRef.current?.thread_id || null,
      in_reply_to: threadRef.current?.in_reply_to || "",
      references: threadRef.current?.references || "",
    };
    // Pre-build the actual /gmail/send payload NOW (before the composer
    // unmounts and the editor/refs are GC'd). The commit closure only
    // needs the payload + the post-send draft cleanup hook.
    const payload = buildSendPayload();
    const draftCleanup = deleteDraft;
    // Mark as "sent" so the autosave hook doesn't recreate a draft on close.
    sentRef.current = true;
    if (typeof window !== "undefined" && window.__closeUndoSend) {
      window.__closeUndoSend({
        snapshot,
        commit: async () => {
          await commitPayload(payload);
          try { await draftCleanup(); } catch (_e) { /* ignore */ }
        },
      });
    } else {
      // Fallback for any host without the toast bridge — fire immediately.
      performSend();
    }
    // Tell the wrapper to close the floating window now.
    onCancel?.();
  };
  const undoSend = () => { /* legacy noop — undo now lives in the toast */ };
  useEffect(() => () => { if (pendingTimerRef.current) clearInterval(pendingTimerRef.current); }, []);
  const onScheduleConfirm = (target) => {
    // Iter 71 — `target` is a Date (local) from SchedulePopover, OR null
    // when callers use the legacy fields. Defensive normalisation:
    let iso = null;
    if (target instanceof Date) {
      iso = target.toISOString();
    } else if (scheduleDate && scheduleTime) {
      const yyyy = scheduleDate.getFullYear();
      const mm = String(scheduleDate.getMonth() + 1).padStart(2, "0");
      const dd = String(scheduleDate.getDate()).padStart(2, "0");
      iso = localToUtcIso(`${yyyy}-${mm}-${dd}`, scheduleTime);
    }
    if (!iso) { toast.error("Invalid date/time."); return; }
    setShowSchedule(false);
    doSend({ scheduleIso: iso });
  };

  if (!editor) return null;

  return (
    <div
      className="flex flex-col h-full relative"
      data-testid="rich-email-composer"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Iter 109 — Drag-and-drop overlay. Visible only while a file
          drag is in progress over the composer. Pointer-events-none on
          the inner panel so the underlying drop target keeps receiving
          the events. */}
      {isDraggingFiles && (
        <div
          className="absolute inset-0 z-50 bg-slate-900/30 backdrop-blur-[1px] flex items-center justify-center pointer-events-none"
          data-testid="composer-drop-overlay"
        >
          <div className="bg-white border-2 border-dashed border-slate-900 rounded-lg px-8 py-6 text-center shadow-lg">
            <Paperclip className="h-7 w-7 text-slate-900 mx-auto mb-2" strokeWidth={1.5} />
            <div className="text-sm font-medium text-slate-900">Drop files to attach</div>
            <div className="text-xs text-slate-500 mt-0.5">Max 24 MB per file · Gmail-compatible</div>
          </div>
        </div>
      )}
      {/* Gmail-style headers — thin underline dividers, label is just plain
          text on the left, no big pill. */}
      <div className="px-4 pt-2 shrink-0 bg-white">
        <div className={ROW_CLS}>
          <span className={ROW_LABEL_CLS}>To</span>
          <div className="flex-1 min-w-0">
            <AddressBookInput
              value={to}
              onChange={setTo}
              placeholder="Recipients"
              dataTestId="composer-to"
            />
          </div>
          {!showCcBcc && (
            <button
              type="button"
              onClick={() => setShowCcBcc(true)}
              className="text-[12px] text-slate-500 hover:text-slate-800 shrink-0"
              data-testid="composer-toggle-ccbcc"
            >
              <span className="font-medium">Cc</span> <span className="font-medium ml-1">Bcc</span>
            </button>
          )}
        </div>
        {showCcBcc && (
          <>
            <div className={ROW_CLS}>
              <span className={ROW_LABEL_CLS}>Cc</span>
              <div className="flex-1 min-w-0">
                <AddressBookInput value={cc} onChange={setCc} placeholder="" dataTestId="composer-cc"/>
              </div>
            </div>
            <div className={ROW_CLS}>
              <span className={ROW_LABEL_CLS}>Bcc</span>
              <div className="flex-1 min-w-0">
                <AddressBookInput value={bcc} onChange={setBcc} placeholder="" dataTestId="composer-bcc"/>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Iter 107 — One-click contact chips for the tagged client.
          Sits flush against the address rows, with its own divider,
          so the contact list is visually grouped with To/Cc/Bcc but
          collapses cleanly when no client is tagged. */}
      <ClientContactChips
        clientId={tagClientId}
        to={to}
        cc={cc}
        bcc={bcc}
        onAddTo={(contact) => setTo((prev) => appendEmail(prev, contact))}
        onAddCc={(contact) => setCc((prev) => appendEmail(prev, contact))}
        onShowCcBcc={() => setShowCcBcc(true)}
      />

      <div className="px-4 pt-2 shrink-0 bg-white">
        {/* Iter 71 — Subject row.
            Bug fix (#2): the shadcn <Input> ships with its own `border`
            plus `bg-background` which rendered a double divider above the
            body editor. We strip ALL native input chrome here so the
            sole separator is the parent row's bottom border (Gmail
            mirrors this layout). */}
        <div className={ROW_CLS}>
          <Input
            value={subject}
            onChange={(e) => { setSubject(e.target.value); onSubjectChange?.(e.target.value); }}
            placeholder="Subject"
            data-testid="composer-subject"
            className="border-0 bg-transparent shadow-none px-0 py-1.5 h-8 text-[13px] leading-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-0 outline-none placeholder:text-slate-400"
          />
          {/* Iter 84 — Audit-trail token chip. Non-editable suffix that
              gets appended to the subject at send time so inbound replies
              route deterministically back to this task/client. The user
              cannot type or delete it from the input — they can only
              remove the underlying task/client tag (which makes the
              composer un-mount + re-mount). */}
          {auditToken?.token && (
            <span
              data-testid="composer-audit-token"
              title={auditToken.kind === "task"
                ? "Replies to this email will be linked to this task"
                : `Replies will be linked to ${auditToken.client_name || "the tagged client"}`}
              className="ml-2 shrink-0 inline-flex items-center text-[11px] font-mono tracking-tight px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200"
            >
              {auditToken.token}
            </span>
          )}
        </div>
      </div>

      {/* TipTap editor */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white">
        <EditorContent editor={editor}/>
        {/* Iter 66b — Quoted chain (reply/forward parent). Lives outside
            Tiptap because the schema would strip the parent body. Renders
            as a Gmail-style collapsible block; included on send. */}
        {quoteHtml && (
          <div className="px-4 pb-4" data-testid="composer-quote-block">
            <button
              type="button"
              data-testid="composer-quote-toggle"
              onClick={() => setQuoteExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 mb-1"
              title={quoteExpanded ? "Hide quoted text" : "Show quoted text"}
            >
              {/* Gmail-style ellipsis */}
              <span className="bg-slate-100 hover:bg-slate-200 rounded px-1.5 py-0.5 leading-none text-slate-500 select-none">···</span>
              <span>{quoteExpanded ? "Hide quoted text" : "Show quoted text"}</span>
            </button>
            {quoteExpanded && (
              <div
                className="text-sm text-slate-700 prose prose-sm max-w-none pl-3 border-l-2 border-slate-200"
                data-testid="composer-quote-preview"
                // Iter 95 — quoteHtml may contain forwarded-message HTML from
                // arbitrary senders. Sanitize through the shared email-HTML
                // profile (same rules as the Triage body renderer).
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(quoteHtml) }}
              />
            )}
          </div>
        )}
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="border-t border-slate-200 px-4 py-2 flex flex-wrap gap-2 bg-slate-50">
          {attachments.map((a, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-md pl-2 pr-1 py-1 text-[11px]"
              data-testid={`composer-attachment-${i}`}
              title={a.forwarded ? "Forwarded attachment — Gmail will fetch the original bytes when sending" : a.filename}
            >
              <Paperclip className="h-3 w-3 text-slate-400"/>
              <span className="font-medium text-slate-700 truncate max-w-[180px]">{a.filename}</span>
              {a.forwarded && (
                <span className="text-[10px] uppercase tracking-wide text-blue-600 bg-blue-50 px-1 rounded">fwd</span>
              )}
              <span className="text-slate-400">{(a.size / 1024).toFixed(0)} KB</span>
              <button
                type="button"
                onClick={() => removeAttachment(i)}
                className="text-slate-400 hover:text-rose-700 ml-0.5"
                data-testid={`composer-attachment-remove-${i}`}
              ><X className="h-3 w-3"/></button>
            </span>
          ))}
        </div>
      )}

      {/* Footer toolbar */}
      <div className="border-t border-slate-200 px-3 py-2 flex items-center gap-1 bg-white shrink-0 flex-wrap">
        {/* Formatting */}
        <div className="inline-flex items-center gap-0.5 border-r border-slate-200 pr-2 mr-1">
          <ToolbarBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" testId="composer-bold"><Bold className="h-3.5 w-3.5"/></ToolbarBtn>
          <ToolbarBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" testId="composer-italic"><Italic className="h-3.5 w-3.5"/></ToolbarBtn>
          <ToolbarBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline" testId="composer-underline"><UnderlineIcon className="h-3.5 w-3.5"/></ToolbarBtn>
          {/* Iter 104 — Strikethrough (built into StarterKit, just needed the button). */}
          <ToolbarBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough" testId="composer-strike"><Strikethrough className="h-3.5 w-3.5"/></ToolbarBtn>
          {/* Iter 106 — Google-Docs-style swatch text colour picker.
              Replaces the native <input type=color> (which had a UA
              min-width that intercepted neighbouring clicks AND looked
              jarring vs the rest of the toolbar). Now a Popover with a
              fixed Google palette + Custom… escape hatch. */}
          <ColorPalettePopover
            value={editor.getAttributes("textStyle").color || ""}
            onPick={(hex) => editor.chain().focus().setColor(hex).run()}
            onReset={() => editor.chain().focus().unsetColor().run()}
            testId="composer-color-trigger"
          />
          <ToolbarBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list" testId="composer-bullet"><List className="h-3.5 w-3.5"/></ToolbarBtn>
          <ToolbarBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list" testId="composer-ordered"><ListOrdered className="h-3.5 w-3.5"/></ToolbarBtn>
          <ToolbarBtn active={editor.isActive("link")} onClick={setLinkPrompt} title="Link" testId="composer-link"><Link2 className="h-3.5 w-3.5"/></ToolbarBtn>
        </div>
        {/* Attach */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={onAttachFiles}
          data-testid="composer-file-input"
        />
        <ToolbarBtn onClick={() => fileInputRef.current?.click()} title="Attach files" testId="composer-attach"><Paperclip className="h-3.5 w-3.5"/></ToolbarBtn>
        {/* Right side: discard + send */}
        <div className="ml-auto flex items-center gap-2">
          {/* Iter 67 — Draft autosave status indicator */}
          {draftStatus && (
            <span className="text-[11px] text-slate-400 tabular-nums" data-testid="draft-status">{draftStatus}</span>
          )}
          <button
            type="button"
            onClick={async () => { await deleteDraft(); onCancel?.(); }}
            data-testid="composer-discard"
            className="p-1.5 rounded text-slate-500 hover:bg-rose-50 hover:text-rose-700"
            title="Discard"
          ><Trash2 className="h-3.5 w-3.5"/></button>
          {/* Send + schedule split button */}
          <div className="inline-flex">
            <Button
              type="button"
              data-testid="composer-send"
              onClick={() => doSend()}
              disabled={!canSend}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-l-md rounded-r-none px-4 h-9"
            >
              <Send className="h-3.5 w-3.5 mr-2"/>Send
            </Button>
            <SchedulePopover
              open={showSchedule}
              onOpenChange={setShowSchedule}
              onConfirm={onScheduleConfirm}
              disabled={!canSend}
              variant="split"
              triggerTestId="composer-schedule-toggle"
              channelLabel="Schedule send"
              helperText="Saves as a Gmail draft now and sends at the time you pick. Cancel any time from the Scheduled folder."
            />
          </div>
        </div>
      </div>

      {/* Iter 85 — In-composer undo banner removed; the undo control now
          lives inside the green sonner "Sent" toast (5s window). The
          composer closes the instant the user clicks Send so they can
          move on to the next task immediately. */}
      {/* Minimal inline styles to make TipTap look like a clean email body */}
      <style>{`
        [data-testid="compose-editor"] p { margin: 0 0 8px 0; }
        [data-testid="compose-editor"] ul, [data-testid="compose-editor"] ol { padding-left: 20px; margin: 0 0 8px 0; }
        [data-testid="compose-editor"] a { color: #2563eb; text-decoration: underline; }
        [data-testid="compose-editor"] p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #94a3b8;
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
